import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from '../cx';
import styles from './Alert.module.css';

export type AlertTone = 'info' | 'warning' | 'danger' | 'success';

export interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  tone?: AlertTone;
  /** Overrides the native `HTMLAttributes.title` (a plain-string tooltip) — an Alert's title is a heading, which needs to accept the same rich content as `children`. */
  title?: ReactNode;
  icon?: ReactNode;
  children: ReactNode;
}

const DEFAULT_ICON: Record<AlertTone, ReactNode> = {
  info: (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 7.2v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="8" cy="4.8" r="0.9" fill="currentColor" />
    </svg>
  ),
  warning: (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 1.5l7 12.5H1z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M8 6.5v3.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="8" cy="12" r="0.9" fill="currentColor" />
    </svg>
  ),
  danger: (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5.5 5.5l5 5m0-5l-5 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  success: (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4" />
      <path d="M4.8 8.2l2.1 2.1 4.3-4.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

/**
 * A static, inline banner for page-level or section-level messaging (form
 * errors, empty-state caveats, background-sync notices). For a transient,
 * self-dismissing message, use Toast instead.
 */
export function Alert({ tone = 'info', title, icon, className, children, ...rest }: AlertProps) {
  return (
    <div className={cx(styles.alert, styles[tone], className)} role={tone === 'danger' ? 'alert' : 'status'} {...rest}>
      <span className={styles.icon}>{icon ?? DEFAULT_ICON[tone]}</span>
      <div className={styles.content}>
        {title && <div className={styles.title}>{title}</div>}
        {children}
      </div>
    </div>
  );
}
